import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { MatchingProfileRow, SeeClientsEnum } from '@uplift/types';
import { ProviderActivationEventName, ProviderActivationStatus, getEthnicities, getFaiths, getGenders, getLanguages, getSpecialties, getSupportedAgeGroups, getTherapeuticStyles, getTherapyTypes, getTreatmentApproaches } from '@uplift/api';
import { useEditableSectionsContext, useAlert } from '@uplift/ui-v2';
import { ProfileContextType, useProfileContext } from './profile.context';
import { FormikConfig, FormikValues } from 'formik';
import { filterOutGroupModality } from '@uplift/utils';
import { useCurrentuser } from '@uplift/contexts';
import { useProviderActivationQuery } from '@uplift/queries';

type AsyncQueryKeys = Extract<MatchingProfileRow,
| MatchingProfileRow.faith
| MatchingProfileRow.ethnicity
| MatchingProfileRow.gender
| MatchingProfileRow.languages
| MatchingProfileRow.supportedAgeGroups
| MatchingProfileRow.modalities
| MatchingProfileRow.specialties
| MatchingProfileRow.treatmentApproach
| MatchingProfileRow.therapeuticStyle
>;

export const PROFILE_ROW_QUERY_KEYS: Record<AsyncQueryKeys, QueryKey> = {
  faith: [ 'faith' ],
  ethnicity: [ 'ethnicities' ],
  gender: [ 'genders' ],
  languages: [ 'languages' ],
  supportedAgeGroups: [ 'supported-age-groups' ],
  modalities: [ 'modalities' ],
  specialties: [ 'specialties' ],
  treatmentApproach: [ 'treatment-approaches' ],
  therapeuticStyle: [ 'therapeutic-styles' ],
};

const profileRowQueryMap = {
  faith: async () => (await getFaiths()).data,
  ethnicity: async () => (await getEthnicities()).data,
  gender: async () => (await getGenders()).data,
  languages: async () => (await getLanguages()).data,
  supportedAgeGroups: async () => (await getSupportedAgeGroups()).ages,
  modalities: async () => (await getTherapyTypes()).therapyTypes?.filter(filterOutGroupModality),
  specialties: async () => (await getSpecialties()).data,
  treatmentApproach: async () => (await getTreatmentApproaches()).data,
  therapeuticStyle: async () => (await getTherapeuticStyles()).data,
};

export const usePreprocessProfileEditKeyChange = () => {
  const queryClient = useQueryClient();

  const preprocess = async (key: MatchingProfileRow) => {
    if (key in PROFILE_ROW_QUERY_KEYS && key in profileRowQueryMap) {
      await queryClient.prefetchQuery(PROFILE_ROW_QUERY_KEYS[key], profileRowQueryMap[key]);
    }
  };

  return preprocess;
};


type ProfileRowKeyToLabel = Record<MatchingProfileRow, string>;

export const profileRowKeyToLabel: ProfileRowKeyToLabel = {
  preferredName: 'Preferred first name',
  pronouns: 'Pronouns',
  gender: 'Gender',
  faith: 'Faith',
  ethnicity: 'Ethnicity',
  languages: 'Spoken languages',
  bio: 'Bio',
  practiceAddress: 'Practice address',
  sessionOption: 'Session option',
  supportedAgeGroups: 'Client ages',
  modalities: 'Types of therapy',
  specialties: 'Specialties',
  communityExpertise: 'Community expertise',
  treatmentApproach: 'Treatment approach',
  therapeuticStyle: 'Therapeutic style',
  education: 'Education',
  acceptedInsurance: 'Accepted insurance',
  licenses: 'Licenses',
  npi: 'NPI',
};

export const PROFILE_QUERY_KEY = (userId: string) => [ 'profile', userId ];
export const PROFILE_FUE_STATUS_QUERY_KEY = (userId: string) => [ 'profile-fue-status', userId ];

export const getFormSuccessMessage = (formTitle: string) => `${formTitle} updated`;
export const getFormErrorMessage = (formTitle: string) => <>There was a problem updating your {formTitle}.<br />Try again or contact <b>help@joinuplift.co</b></>;

export type UseProfileFormHandlersParams = {
  onFormSubmitSuccess?: () => Promise<void> | void;
  onFormSubmitError?: () => void;
};

export const useProfileFormHandlers = (params?: UseProfileFormHandlersParams) => {
  const { profile } = useProfileContext();
  const { setEditKey, editKey } = useEditableSectionsContext();
  const queryClient = useQueryClient();
  const alert = useAlert();

  const formTitle = profileRowKeyToLabel[editKey] ?? 'Profile';

  const onFormSubmitSuccess = async () => {
    if (params?.onFormSubmitSuccess) {
      await params.onFormSubmitSuccess();
      return;
    }

    alert.success(getFormSuccessMessage(formTitle));
    await queryClient.invalidateQueries(PROFILE_QUERY_KEY(profile.user.id));
    setEditKey(null);
  };

  const onFormSubmitError = () => {
    if (params?.onFormSubmitError) {
      return params.onFormSubmitError();
    }

    alert.error(getFormErrorMessage(formTitle));
  };

  const withFormHandlers = <T extends FormikValues>(onSubmit: FormikConfig<T>['onSubmit']) => (...onSubmitParams: Parameters<FormikConfig<T>['onSubmit']>) =>
    Promise.resolve(onSubmit(...onSubmitParams)).then(async () => await onFormSubmitSuccess()).catch(onFormSubmitError);

  return { onFormSubmitSuccess, onFormSubmitError, withFormHandlers };
};

export const seeClientsTypeLabel: Record<SeeClientsEnum, string> = {
  [SeeClientsEnum.inPerson]: 'In-person only',
  [SeeClientsEnum.telehealth]: 'Virtual only',
  [SeeClientsEnum.videoAndInPerson]: 'Virtual and In-person',
};

/*
 Try to keep these in order as they appear in the profile page
*/
export const getInvalidRows = (profile: ProfileContextType['profile'] | undefined) => {
  const invalidRows: (MatchingProfileRow | 'photo')[] = [];

  if (!profile?.avatarAsset) {
    invalidRows.push('photo');
  }

  if (!profile?.profile?.shortBio) {
    invalidRows.push(MatchingProfileRow.bio);
  }

  if (!profile?.locations?.length) {
    invalidRows.push(MatchingProfileRow.practiceAddress);
  }

  if (!profile?.supportedAgeGroups?.length) {
    invalidRows.push(MatchingProfileRow.supportedAgeGroups);
  }

  if (!profile?.modalities?.filter(filterOutGroupModality)?.length) {
    invalidRows.push(MatchingProfileRow.modalities);
  }

  if (!profile?.specialties?.length) {
    invalidRows.push(MatchingProfileRow.specialties);
  }

  if (!profile?.treatmentApproaches?.length) {
    invalidRows.push(MatchingProfileRow.treatmentApproach);
  }

  if (!profile?.qualification?.degrees?.length) {
    invalidRows.push(MatchingProfileRow.education);
  }

  return invalidRows;
};

export const enum ProfileOnboardingStatus {
  ready_to_submit = 'ready_to_submit',
  incomplete = 'incomplete',
  in_review = 'in_review',
  approved = 'approved',
}

export const useProfileOnboardingStatus = (profile: ProfileContextType['profile'] | undefined) => {
  const me = useCurrentuser();
  const { data: providerActivation, isInitialLoading: isLoading } = useProviderActivationQuery(me.id);

  let profileOnboardingStatus: ProfileOnboardingStatus | null = null;

  if (providerActivation && providerActivation.status === ProviderActivationStatus.onboarding) {
    if (providerActivation.events.some(event => event.eventName === ProviderActivationEventName.matchingProfileApproved)) {
      profileOnboardingStatus = ProfileOnboardingStatus.approved;
    } else if (providerActivation.events.some(event => event.eventName === ProviderActivationEventName.matchingProfileSubmitted)) {
      profileOnboardingStatus = ProfileOnboardingStatus.in_review;
    } else {
      profileOnboardingStatus = ProfileOnboardingStatus.ready_to_submit;

      if (getInvalidRows(profile).length) {
        profileOnboardingStatus = ProfileOnboardingStatus.incomplete;
      }
    }
  }

  return {
    isLoading,
    profileOnboardingStatus,
  };
};

export const useDisplayInvalidRows = () => {
  const me = useCurrentuser();
  const { data: providerActivation } = useProviderActivationQuery(me.id);
  const { profile } = useProfileContext();

  if (!providerActivation || providerActivation.status !== ProviderActivationStatus.onboarding) {
    return [];
  }

  return getInvalidRows(profile);
};
